import React, { FC, useContext, useEffect, useState } from "react";
import ReactAudioPlayer from "react-audio-player";
import ReactPlayer from "react-player";
import { toast } from "react-toastify";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, Copy, X } from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import CustomJoyride from "components/atoms/Joyride";
import SocialShare from "components/atoms/SocialShare";
import { useTutorial } from "components/organisms/TutorialProvider";
import { AppContext } from "lib/contexts";
import { ImageDTO } from "types";
import { shareModalTutorialSteps } from "utils/joyride/shareModalTutorialSteps";
import { ModalContent, ModalOverlay } from "./ShareModal.styled";
interface ShareModalProps {
  isOpen: boolean;
  image: ImageDTO | null;
  onClose: () => void;
}
const ShareModal: FC<ShareModalProps> = ({
  isOpen,
  image,
  onClose
}) => {
  const {
    state
  } = useContext(AppContext);
  const [copied, setCopied] = useState(false);
  const {
    tutorialState,
    tutorialStateInitialized,
    startTutorial,
    completeTutorial
  } = useTutorial();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState) return;
    if (isOpen && tutorialState.controlsContainer.completed && !tutorialState.shareModal.completed && !tutorialState.shareModal.started) {
      startTutorial("shareModal");
    }
  }, [isOpen, tutorialState, startTutorial, tutorialStateInitialized]);
  useEffect(() => {
    if (tutorialState?.shareModal.started && !tutorialState.shareModal.completed) {
      setRun(true);
    }
  }, [tutorialState]);
  const handleJoyrideCallback = (data: any) => {
    const {
      status,
      action,
      type
    } = data;
    if (["finished", "skipped"].includes(status)) {
      completeTutorial("shareModal");
      setRun(false);
      setStepIndex(0);
      onClose();
    } else if (type === "step:after") {
      if (action === "next") {
        setStepIndex(prev => prev + 1);
      } else if (action === "prev") {
        setStepIndex(prev => prev - 1);
      }
    }
  };
  if (!image) {
    return null;
  }
  const referralCode = state.referralCode;
  const shareUrl = `https://char-gen.com/images/${image.id}${referralCode ? `?referralCode=${referralCode}` : ""}`;
  const copyToClipboard = async () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link Copied to Clipboard!", {
      position: "bottom-center"
    });
    setTimeout(() => setCopied(false), 2000);
  };
  const isVideo = image?.image.endsWith(".mp4");
  const isAudio = image?.image.endsWith(".mp3");
  if (!isOpen) return null;
  return <DialogPrimitive.Root open={isOpen} onOpenChange={isOpen => {
    if (!isOpen) {
      onClose();
    }
  }} data-sentry-element="unknown" data-sentry-component="ShareModal" data-sentry-source-file="index.tsx">
            <DialogPrimitive.Portal data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                {" "}
                {run && <CustomJoyride steps={shareModalTutorialSteps} run={run} stepIndex={stepIndex} disableScrolling={false} callback={handleJoyrideCallback} />}
                <ModalOverlay className="joyride-share-modal" data-sentry-element="ModalOverlay" data-sentry-source-file="index.tsx" />
                <ModalContent aria-describedby="modal-description" className="max-h-[90vh] w-full max-w-2xl" data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
                    <div className="relative mx-4 my-4 rounded-lg bg-gradient-to-b from-gray-800 to-gray-900 p-6 shadow-xl overflow-y-auto max-h-[calc(90vh-2rem)]">
                        {/* Close Button */}
                        <button onClick={onClose} className="absolute right-4 top-4 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white joyride-close-share-modal">
                            <X className="h-6 w-6" data-sentry-element="X" data-sentry-source-file="index.tsx" />
                        </button>

                        {/* Title */}
                        <h2 className="mb-6 text-center text-2xl font-bold text-white">
                            Share this creation
                        </h2>

                        {/* Image */}
                        <div className="mb-6 overflow-hidden rounded-lg border border-gray-700">
                            {isVideo ? <ReactPlayer url={image.image} playing controls={true} width="100%" height="100%" loop muted playsinline /> : isAudio ? <div className="w-full h-full flex items-center justify-center">
                                    <ReactAudioPlayer src={image.image} autoPlay={false} controls loop />
                                </div> : <Image src={image.image} alt={"Share Preview"} width={500} height={500} objectFit="cover" />}
                        </div>

                        {/* Copy Link Section */}
                        <div className="mb-6 flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 p-3">
                            <div className="flex-1 truncate text-sm text-gray-300 joyride-share-url">
                                {shareUrl}
                            </div>
                            <button onClick={copyToClipboard} className={cn("flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors joyride-copy-link-button", copied ? "bg-green-600 text-white" : "bg-gray-700 text-gray-200 hover:bg-gray-600")}>
                                {copied ? "Copied!" : "Copy Link"}
                                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            </button>
                        </div>

                        {/* Description */}
                        <p className="mb-6 text-center text-sm text-gray-300">
                            {image.imageMetadata.description}
                        </p>

                        {/* Rewards Info */}
                        <div className="mb-6 rounded-lg bg-gradient-to-r from-amber-500/10 to-yellow-500/10 p-4">
                            <p className="mb-2 text-center text-sm font-medium text-amber-400">
                                🎁 Share and earn rewards!
                            </p>
                            <p className="text-center text-xs text-gray-300 joyride-share-instructions">
                                Share any image and tag @chargen_ai on Instagram
                                or TikTok (or just share on other social media)
                                to earn 10 free gold! Plus, when friends sign up
                                using your link, you both get 100 gold!
                            </p>
                        </div>

                        {/* Social Links */}
                        <div className="flex justify-center gap-4">
                            <SocialShare image={image} description={image.imageMetadata.description} data-sentry-element="SocialShare" data-sentry-source-file="index.tsx" />
                        </div>
                    </div>
                </ModalContent>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>;
};
export default ShareModal;